/**
 * Formatting for scenario figures. Executive copy: $4.4M rather than
 * 4420569.6, 84.4% rather than 84.37, and a sign on every change.
 */
import type {
  ScenarioLeverUnit,
  ScenarioValueUnit,
  WhatIfPeriod,
} from "../../services/api";

export const PERIOD_LABELS: Record<WhatIfPeriod, string> = {
  last_month: "Last month",
  last_quarter: "Last quarter",
  year_to_date: "Year to date",
  last_12_months: "Last 12 months",
  all_available: "All available data",
};

const COMPACT = [
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "K"],
] as const;

function compact(magnitude: number): string {
  for (const [size, suffix] of COMPACT) {
    if (magnitude >= size) {
      const scaled = magnitude / size;
      const digits = scaled >= 100 ? 0 : 1;
      return `${scaled.toFixed(digits).replace(/\.0$/, "")}${suffix}`;
    }
  }
  return magnitude.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function isNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** A duration in seconds as people say it: 45 s, 8 min, 7 min 45 s. */
export function formatSeconds(seconds: number): string {
  const whole = Math.round(seconds);
  if (whole < 60) return `${whole} s`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`;
}

/** A scenario value in its unit, compact for large amounts. */
export function formatAmount(
  value: number | null | undefined,
  unit: ScenarioValueUnit | ScenarioLeverUnit,
): string {
  if (!isNumber(value)) return "—";
  const sign = value < 0 ? "-" : "";
  const magnitude = Math.abs(value);
  switch (unit) {
    case "currency":
      return `${sign}$${compact(magnitude)}`;
    case "currency_per_hour":
      return `${sign}$${magnitude.toLocaleString("en-US", { maximumFractionDigits: 0 })}/h`;
    case "percent":
      return `${sign}${magnitude.toFixed(1).replace(/\.0$/, "")}%`;
    case "points":
      return `${sign}${magnitude.toFixed(1).replace(/\.0$/, "")} pts`;
    case "hours":
      return `${sign}${compact(magnitude)} h`;
    case "months":
      return `${sign}${magnitude.toFixed(1).replace(/\.0$/, "")} mo`;
    case "seconds":
      return `${sign}${formatSeconds(magnitude)}`;
    case "count":
    default:
      return `${sign}${magnitude >= 1e4 ? compact(magnitude) : magnitude.toLocaleString("en-US", { maximumFractionDigits: 1 })}`;
  }
}

/** A change, always signed, in the unit's own terms. Percent becomes points. */
export function formatDelta(
  value: number | null | undefined,
  unit: ScenarioValueUnit,
): string {
  if (!isNumber(value)) return "—";
  if (value === 0) return "No change";
  const sign = value > 0 ? "+" : "";
  if (unit === "percent") {
    return `${sign}${value.toFixed(1)} pts`;
  }
  return `${sign}${formatAmount(value, unit)}`;
}

/**
 * A lever's current value as the label beside its slider. A lever that can go
 * negative, such as a price change, is signed so +3% and -3% read differently.
 */
export function formatLever(
  value: number,
  unit: ScenarioLeverUnit,
  signed = false,
): string {
  switch (unit) {
    case "percent":
      return `${signed && value > 0 ? "+" : ""}${value}%`;
    case "points":
      return `+${value} pts`;
    case "currency":
      return `$${value.toLocaleString("en-US")}`;
    case "currency_per_hour":
      return `$${value.toLocaleString("en-US")}/h`;
    case "hours":
      return `${value} h`;
    case "months":
      return `${value} mo`;
    case "seconds":
      return formatSeconds(value);
    case "count":
    default:
      return value.toLocaleString("en-US");
  }
}

/**
 * Whether a change is good news. Null when the direction carries no judgement,
 * so the interface shows it neutrally rather than guessing a colour.
 */
export function deltaTone(
  delta: number | null | undefined,
  higherIsBetter: boolean | null | undefined,
): "favourable" | "unfavourable" | "neutral" {
  if (!isNumber(delta) || delta === 0 || higherIsBetter == null) return "neutral";
  return (delta > 0) === higherIsBetter ? "favourable" : "unfavourable";
}
